import api, { apiClient } from './api';

/**
 * PDF Sign Request interface
 */
export interface PDFSignRequest {
    house_id: string;
    document_type: 'check_in' | 'check_out';
    language: 'es' | 'en' | 'nl' | 'fr';
    deposit_amount: number;
    client_name?: string;
    additional_notes?: string;
    date?: string;
}

/**
 * PDF Sign Response interface
 */
export interface PDFSignResponse {
    success: boolean;
    error?: string;
    pdf_path?: string;
    filename?: string;
    message?: string;
}

/**
 * PDF Sign Service
 * Handles all PDF signing related API requests
 */
const pdfSignService = {
    /**
     * Generate a signed PDF deposit receipt
     * @param request - PDF sign request data
     * @returns Promise<PDFSignResponse>
     */
    generate: async (request: PDFSignRequest): Promise<PDFSignResponse> => {
        try {
            console.log('pdfSignService.generate - Request:', request);
            const response = await api.post<PDFSignResponse>('/pdf-sign/generate', request);
            console.log('pdfSignService.generate - Response:', response);
            return response;
        } catch (error: any) {
            console.error('pdfSignService.generate - Error:', error);

            // Extract error message from response
            const errorMessage = error?.response?.data?.error || error?.message || 'Failed to generate PDF';

            return {
                success: false,
                error: errorMessage,
            };
        }
    },

    /**
     * Download a generated PDF by filename
     * @param filename - PDF filename
     * @returns Promise<Blob>
     */
    download: async (filename: string): Promise<void> => {
        try {
            console.log('pdfSignService.download - Filename:', filename);

            const response = await apiClient.get(`/pdf-sign/download/${filename}`, {
                responseType: 'blob', // Important for file download
            });

            // Create a blob from the response
            const blob = new Blob([response.data], { type: 'application/pdf' });

            // Create a temporary URL for the blob
            const url = window.URL.createObjectURL(blob);

            // Create a temporary link element and trigger download
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();

            // Cleanup
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            console.log('pdfSignService.download - Download completed');
        } catch (error) {
            console.error('pdfSignService.download - Error:', error);
            throw error;
        }
    },

    /**
     * Generate and immediately download PDF
     * @param request - PDF sign request data
     * @returns Promise<PDFSignResponse>
     */
    generateAndDownload: async (request: PDFSignRequest): Promise<PDFSignResponse> => {
        try {
            // First generate the PDF
            const generateResponse = await pdfSignService.generate(request);

            if (generateResponse.success && generateResponse.filename) {
                // Then download it
                await pdfSignService.download(generateResponse.filename);
            }

            return generateResponse;
        } catch (error: any) {
            console.error('pdfSignService.generateAndDownload - Error:', error);
            return {
                success: false,
                error: error?.message || 'Failed to generate and download PDF',
            };
        }
    },
};

export default pdfSignService;
